import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, ExternalLink, Bookmark, BookmarkCheck } from "lucide-react";
import ShareButtons from "@/components/ShareButtons";
import { Link } from "react-router-dom";
import { useArticleBookmarks } from "@/hooks/useArticleBookmarks";
import { Button } from "@/components/ui/button";
import { decodeHtmlEntities } from "@/lib/utils";

interface NewsCardProps {
  id: string;
  headline: string;
  summary: string[];
  insight: string;
  source: string;
  timestamp: string;
  url?: string;
  imageUrl?: string | null;
  category?: string;
  publishedAt?: string;
}

const NewsCard = ({ id, headline, summary, insight, source, timestamp, url, imageUrl, category, publishedAt }: NewsCardProps) => {
  const { isBookmarked, toggleBookmark } = useArticleBookmarks();
  const bookmarked = isBookmarked(id);
  
  // Check if article is breaking news (published within last 30 minutes)
  const isBreakingNews = publishedAt ? 
    (Date.now() - new Date(publishedAt).getTime()) < 30 * 60 * 1000 : 
    false;

  return (
    <Card className="overflow-hidden hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 relative">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 z-10 bg-background/80 backdrop-blur-sm hover:bg-background"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleBookmark(id);
        }}
        aria-label={bookmarked ? "Remove bookmark" : "Add bookmark"}
      >
        {bookmarked ? (
          <BookmarkCheck className="h-5 w-5 text-primary fill-primary" />
        ) : (
          <Bookmark className="h-5 w-5" />
        )}
      </Button>
      <Link to={`/news/${id}`} className="block md:flex">
        {imageUrl && (
          <div className="md:w-64 md:flex-shrink-0">
            <img 
              src={imageUrl} 
              alt={decodeHtmlEntities(headline)}
              className="w-full h-48 md:h-full object-cover"
              loading="lazy"
            />
          </div>
        )}
        <div className="p-6 space-y-4 flex-1">
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {source}
                </Badge>
                {category && category !== 'general' && (
                  <Badge variant="secondary" className="text-xs capitalize">
                    {category}
                  </Badge>
                )}
                {isBreakingNews && (
                  <Badge className="text-xs bg-red-500 hover:bg-red-600 animate-pulse">
                    Breaking
                  </Badge>
                )}
              </div>
              {url && (
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  Read Original
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            <h3 className="text-xl font-semibold mb-2 line-clamp-2 hover:text-primary transition-colors">
              {decodeHtmlEntities(headline)}
            </h3>
          </div>
        
        <div className="space-y-2">
          <p className="text-sm font-medium text-primary">Key Takeaways:</p>
          <ul className="space-y-1">
            {summary.map((point, idx) => (
              <li key={idx} className="text-sm text-muted-foreground flex gap-2">
                <span className="text-primary">•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="border-l-2 border-primary pl-4">
          <p className="text-sm font-medium text-primary mb-1">AI Insight:</p>
          <p className="text-sm text-foreground italic">{insight}</p>
        </div>
        
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{timestamp}</span>
            </div>
            <div onClick={(e) => e.preventDefault()}>
              <ShareButtons headline={headline} articleId={id} />
            </div>
          </div>
        </div>
      </Link>
    </Card>
  );
};

export default NewsCard;
